import { useState } from 'react';
import { useDispatch } from 'react-redux';

import { BACKGROUND, PEARL, PRIMARY, RED, TEXT } from '../../../shared/Helpers/colors';
import { DayProperties } from '../Helpers/interfaces';

import { DispatchProperties, useSelector } from '../../../redux/store';
import { AdminTypes } from '../../../components/Menu';
import { isPastTime } from '../Helpers/functions';
import {
	setAddScheduledSession,
	setCopyEvent,
	setDeleteEvent,
	setSelectedDay,
} from '../../../redux/State/clientSlice/calendarHelperSlice';
import { Text } from '../../../shared/uiComponents';
import { Copy, InfoSvg } from '../../../shared/Assets/svgIcons';
import { Add, TrashEmpty } from '../../../shared/Assets/sessionDetailsSVGs';
import { useScreenWidth } from '../../../shared/Helpers/hooks';

// Helper function to determine admin actions positioning based on day position
const getAdminActionsPosition = (dayIndex: number) => {
	const weekIndex = Math.floor(dayIndex / 7);
	const dayInWeek = dayIndex % 7;

	// First 3 weeks: bottom positioning, rest: top positioning
	const isBottomWeek = weekIndex < 3;

	// First 3 days of week: left side, rest: right side (reversed from original)
	const isLeftSide = dayInWeek < 3;

	return {
		positionClass: `${isBottomWeek ? 'bottom' : 'top'}-${isLeftSide ? 'left' : 'right'}`,
	};
};

const SessionHeader = ({ item, dayIndex }: { dayIndex: number; item: DayProperties }) => {
	const dispatch = useDispatch<DispatchProperties>();
	const screenWidth = useScreenWidth();
	const isMobile = screenWidth <= 768;

	const today = new Date();

	const currentDay = today.getDate();
	const currentMonth = today.getMonth();
	const isCurrentMonthDay = currentMonth === item.month;
	const isToday = currentDay === item.day && isCurrentMonthDay;

	const textColor = isToday ? BACKGROUND.PRIMARY[100] : isCurrentMonthDay ? PEARL.BASE[500] : TEXT.QUATERNARY;
	const textBackgroundColor = isToday ? PRIMARY.LIGHT[32] : BACKGROUND.PRIMARY[100];

	const [openActions, setOpenActions] = useState<boolean>(false);

	const role = useSelector((state) => state.account.role);
	const roleRestriction = useSelector((state) => state.account.roleRestriction);

	const isAdmin = role?.section.id === AdminTypes.admin;
	const allowedAfterDate = new Date(item.year, item.month, item.day);
	allowedAfterDate.setDate(allowedAfterDate.getDate() + roleRestriction);
	const isPast = isPastTime(today, allowedAfterDate) && !isAdmin;

	const toggleAdminActionsHandler = () => setOpenActions(!openActions);

	const openDeleteEventsHandler = () => {
		dispatch(setDeleteEvent(true));
		setOpenActions(false);
	};

	const openCopyEventsHandler = () => {
		dispatch(setCopyEvent(true));
		setOpenActions(false);
	};

	const openAddSessionHandler = () => {
		dispatch(setAddScheduledSession(true));
		dispatch(setSelectedDay(item));
		setOpenActions(false);
	};

	// Get positioning information for admin actions
	const { positionClass } = getAdminActionsPosition(dayIndex);

	return (
		<div className="header">
			<Text
				title={item.day}
				fontWeight="semiBold"
				color={textColor}
				type={isMobile ? 'pxs' : 'psm'}
				style={{
					backgroundColor: textBackgroundColor,
					padding: '0 8px',
					borderRadius: '999px',
				}}
			/>
			<div className="sessionHeaderActions">
				<div className="icon">
					<InfoSvg onClick={toggleAdminActionsHandler} />
					<div
						style={{ display: openActions ? 'flex' : 'none' }}
						className={`adminActions adminActions-${positionClass}`}
					>
						{!isPast && (
							<div className="actionRow" onClick={openAddSessionHandler}>
								<div className="actionIcon">
									<Add />
								</div>
								<div className="actionContent">
									<Text title="Add session" type="psm" fontWeight="medium" />
								</div>
							</div>
						)}
						<div className="actionRow" onClick={openCopyEventsHandler}>
							<div className="actionIcon">
								<Copy />
							</div>
							<div className="actionContent">
								<Text title="Copy event(s)" type="psm" fontWeight="medium" />
							</div>
						</div>
						<div className="actionRow" onClick={openDeleteEventsHandler}>
							<div className="actionIcon">
								<TrashEmpty color={RED.BASE} />
							</div>
							<div className="actionContent">
								<Text title="Remove event(s)" type="psm" fontWeight="medium" />
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};

export default SessionHeader;
